import { useState, useEffect, useCallback } from 'react';
import { CvTemplate, CreateCvTemplateData, UpdateCvTemplateData } from '@/types/cvTemplate';
import { CvTemplateService } from '@/services/cvTemplateService';
import { useToast } from '@/hooks/use-toast';

export const useCvTemplates = () => {
	const [templates, setTemplates] = useState<CvTemplate[]>([]);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const { toast } = useToast();

	const fetchTemplates = useCallback(async () => {
		setLoading(true);
		setError(null);
		try {
			const data = await CvTemplateService.getAll();
			setTemplates(data);
		} catch (err) {
			const errorMessage = err instanceof Error ? err.message : 'Failed to fetch CV templates';
			setError(errorMessage);
			toast({
				title: "Error",
				description: errorMessage,
				variant: "destructive",
			});
		} finally {
			setLoading(false);
		}
	}, [toast]);

	const createTemplate = useCallback(async (templateData: CreateCvTemplateData) => {
		try {
			const newTemplate = await CvTemplateService.create(templateData);
			setTemplates(prev => [newTemplate, ...prev]);
			toast({
				title: "Success",
				description: "CV template created successfully",
			});
			return newTemplate;
		} catch (err) {
			const errorMessage = err instanceof Error ? err.message : 'Failed to create CV template';
			toast({
				title: "Error",
				description: errorMessage,
				variant: "destructive",
			});
			throw err;
		}
	}, [toast]);

	const updateTemplate = useCallback(async (id: string, templateData: UpdateCvTemplateData) => {
		try {
			const updatedTemplate = await CvTemplateService.update(id, templateData);
			setTemplates(prev =>
				prev.map(template =>
					template.id === id ? updatedTemplate : template
				)
			);
			toast({
				title: "Success",
				description: "CV template updated successfully",
			});
			return updatedTemplate;
		} catch (err) {
			const errorMessage = err instanceof Error ? err.message : 'Failed to update CV template';
			toast({
				title: "Error",
				description: errorMessage,
				variant: "destructive",
			});
			throw err;
		}
	}, [toast]);

	const deleteTemplate = useCallback(async (id: string) => {
		try {
			await CvTemplateService.delete(id);
			setTemplates(prev => prev.filter(template => template.id !== id));
			toast({
				title: "Success",
				description: "CV template deleted successfully",
			});
		} catch (err) {
			const errorMessage = err instanceof Error ? err.message : 'Failed to delete CV template';
			toast({
				title: "Error",
				description: errorMessage,
				variant: "destructive",
			});
			throw err;
		}
	}, [toast]);

	const duplicateTemplate = useCallback(async (id: string, newName?: string) => {
		try {
			const duplicatedTemplate = await CvTemplateService.duplicate(id, newName);
			setTemplates(prev => [duplicatedTemplate, ...prev]);
			toast({
				title: "Success",
				description: "CV template duplicated successfully",
			});
			return duplicatedTemplate;
		} catch (err) {
			const errorMessage = err instanceof Error ? err.message : 'Failed to duplicate CV template';
			toast({
				title: "Error",
				description: errorMessage,
				variant: "destructive",
			});
			throw err;
		}
	}, [toast]);

	useEffect(() => {
		fetchTemplates();
	}, [fetchTemplates]);

	return {
		templates,
		loading,
		error,
		fetchTemplates,
		createTemplate,
		updateTemplate,
		deleteTemplate,
		duplicateTemplate,
	};
};
